import { Injectable } from "@angular/core";

// BFX
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  SystemConfigPromiseService,
  SysFeatures,
  CcyPairPromiseLookupService,
  CcyPairLookupService,
  MarketTimePromiseLookupService,
  MarketTimeLookupService,
} from "shared/cubiq-services";
import { TranslateService } from "@ngx-translate/core";
import { BfxAutocompleteGenericModel } from "app/bfx-interface-controls/models/bfx-interface-controls.models";
import { GoodFieldType } from "app/bfx-user-settings/model/bfx-order-entry-defaults.model";
import { OETimeMachineService } from "app/order-entry/services/basic/oe-time-machine.service";
import { OEValidatorService } from "app/order-entry/services/oe-validator.service";
import { ICAutocompleteGenericModel } from "app/order-entry/interface/controls/models/bfx-interface-controls.models";

@Injectable()
export class BfxOrderEntryDefaultsService {
  private goodFromAll: Array<ICAutocompleteGenericModel>;
  private goodFromValid: Array<ICAutocompleteGenericModel>;
  private goodUntilAll: Array<ICAutocompleteGenericModel>;
  private goodUntilValid: Array<ICAutocompleteGenericModel>;

  /**********************************************************************/
  /* CONSTRUCTOR  */
  constructor(
    protected translate: TranslateService,
    protected marketTimePromiseService: MarketTimePromiseLookupService,
    protected timeMachine: OETimeMachineService,
    protected validator: OEValidatorService,
  ) {
    this.initializeMarketTimeDatasets();
  }

  private initializeMarketTimeDatasets(): void {
    this.marketTimePromiseService.isPrimed().then(() => {
      // Create 2 datasets for both goodFrom and goodUntil - one for valid constants
      // and one for all (so we can allow restoring of invalid values)

      const goodFromAll: Array<ICAutocompleteGenericModel> = [];
      const goodFromValid: Array<ICAutocompleteGenericModel> = [];
      const goodUntilAll: Array<ICAutocompleteGenericModel> = [];
      const goodUntilValid: Array<ICAutocompleteGenericModel> = [];

      // Only for Good Until add DAY and GTC
      let model = new ICAutocompleteGenericModel("DAY", "DAY");
      goodUntilValid.push(model);
      goodUntilAll.push(model);
      model = new ICAutocompleteGenericModel("GTC", "GTC");
      goodUntilAll.push(model);
      goodUntilValid.push(model);

      model = new ICAutocompleteGenericModel("NOW", "NOW");
      goodFromAll.push(model);
      goodFromValid.push(model);

      // add market times
      let lookup: MarketTimeLookupService = this.marketTimePromiseService.getBase();
      const tm = this.timeMachine;
      if (lookup != null) {
        const allMarketTimes: Array<CubiqTS.MarketTimeConstantImpl> = lookup
          .getAll()
          .filter(
            (marketTimeConstant: CubiqTS.MarketTimeConstantImpl) =>
              marketTimeConstant instanceof CubiqTS.MarketTimeConstantImpl,
          );

        allMarketTimes.forEach((marketTime: CubiqTS.MarketTimeConstantImpl) => {
          if (marketTime.id?.toNumber() >= 0) {
            const valueModel = new ICAutocompleteGenericModel(
              marketTime.id instanceof CubiqTS.Long ? marketTime.id.toString() : marketTime.id,
              marketTime.constantName,
            );
            const goodModel = tm.createModelFromMarketTimeConstantImpl(marketTime);
            const isValid = !this.validator.isGoodFromInThePast(goodModel);

            goodFromAll.push(valueModel);
            goodUntilAll.push(valueModel);
            if (isValid) {
              goodFromValid.push(valueModel);
              goodUntilValid.push(valueModel);
            }
          }
        });
      }
      this.goodFromValid = goodFromValid;
      this.goodFromAll = goodFromAll;
      this.goodUntilValid = goodUntilValid;
      this.goodUntilAll = goodUntilAll;
    });
  }

  /**
   * Populate the goodFrom and goodUntil types with correct domain values
   * @param goodFrom
   * @param goodUntil
   * @returns
   */
  public populateGoodTimeFieldSettings(
    goodFrom: GoodFieldType,
    goodUntil: GoodFieldType,
  ): Promise<void> {
    return this.marketTimePromiseService.isPrimed().then(() => {
      goodFrom.label = this.translate.instant(goodFrom.labelKey);
      goodUntil.label = this.translate.instant(goodUntil.labelKey);
      goodFrom.tooltip = this.translate.instant(goodFrom.tooltipKey);
      goodUntil.tooltip = this.translate.instant(goodUntil.tooltipKey);

      goodFrom.validDataset = this.goodFromValid.concat();
      goodFrom.allDataset = this.goodFromAll.concat();
      goodFrom.suggestions = this.goodFromValid.concat();

      goodUntil.validDataset = this.goodUntilValid.concat();
      goodUntil.allDataset = this.goodUntilAll.concat();
      goodUntil.suggestions = this.goodUntilValid.concat();
    });
  }
}
